import { Link, useHistory } from '@jupiter/plugin-runtime/router';
import { IconPlus, IconHash, IconCrossStroked } from '@douyinfe/semi-icons';
import cls from 'classnames';
import {
  Button,
  Empty,
  Popconfirm,
  Space,
  Spin,
  Toast,
} from '@douyinfe/semi-ui';

import Title from '@douyinfe/semi-ui/lib/es/typography/title';
import Text from '@douyinfe/semi-ui/lib/es/typography/text';
import { Rightarrow } from '@okee-fe/react-icons-dy-open';
import style from './index.module.scss';
import {
  useInsertGroupModal,
  useInsertPostGroupModal,
  useDeletePostGroup,
  useDeleteGroup,
  useGetPostsByGroup,
} from './hooks';
import useBiz from '@/context/useBiz';
import { useUserInfo } from '@/hooks/useUserInfo';
import {
  GetPostsByGroupResponseData,
  Group,
  GroupType,
} from '@/typings/open.platform.forum_bff/namespaces/forumpost';
import { STD_DATE_FORMAT_RE, OFFICIAL } from '@/consts';
import { useScope } from '@/hooks/useScope';
import { Scope } from '@/typings/fe';

const ellipsis = {
  rows: 1,
};

const renderNoticeItem = (notice: Group, ifAdmin: boolean, scope: Scope) => {
  if (notice.PostList.length > 0) {
    return notice.PostList.map(item => (
      <li className={cls('mb-4')} key={item.Id}>
        <div className="flex justify-between">
          <Link
            target={'_blank'}
            to={`/${scope}/post/${item.Id}`}
            className="text-semi-color-text-0 hover:text-semi-color-primary">
            <Text
              ellipsis={true}
              style={{ color: 'inherit' }}
              className={'break-all max-w-[296px] text-base'}>
              {item.Title}
            </Text>
          </Link>
          <span className="text-semi-color-text-2 text-base ml-auto">
            {item.UpdatedAt.replace(STD_DATE_FORMAT_RE, '$2/$3')}
          </span>
        </div>
      </li>
    ));
  }
  if (ifAdmin) {
    return <Empty className="w-[100px]" />;
  }
  return null;
};

function renderOtherItem(
  group: Group,
  ifAdmin: boolean,
  scope: Scope,
  deletePostGroup: (id: string, groupId: string) => Promise<void>,
  refetch: () => Promise<any>,
) {
  if (group.PostList.length > 0) {
    return group.PostList.map(item => (
      <div
        className={cls('mb-4 flex items-center text-base leading-5 h-5')}
        key={item.Id}>
        <div className={cls(style.link)}>
          <Link target={'_blank'} to={`/${scope}/post/${item.Id}`} className="">
            <Text
              ellipsis={true}
              style={{ color: 'inherit' }}
              className="break-all max-w-[19rem]">
              {item.Title}
            </Text>
          </Link>
        </div>

        {ifAdmin && (
          <Popconfirm
            position="top"
            title="确认删除该帖子吗？"
            onConfirm={async () => {
              await deletePostGroup(item.Id, group.Id);
              await refetch();
              Toast.success('删除成功');
            }}>
            <div className=" ml-auto text-semi-color-text-2 text-base leading-5 h-5">
              <IconCrossStroked className="text-semi-color-text-1 text-base cursor-pointer ml-auto  hover:text-semi-color-link" />
            </div>
          </Popconfirm>
        )}
      </div>
    ));
  }
  if (ifAdmin) {
    return <Empty style={{ height: 100 }} />;
  }
  return null;
}

const Panel: React.FC = ({ children }) => (
  <div className="mb-4 rounded-xl bg-white py-6 px-8 w-[400px]">{children}</div>
);

const NoticeBoard: React.FC<{
  notice?: Group;
}> = ({ notice }) => {
  const { ifAdmin } = useUserInfo();
  const scope = useScope();
  return (
    <>
      {notice && (
        <Panel key={notice.Id}>
          <div className="flex mr-auto mb-4 justify-between">
            <div className="font-medium text-xl flex items-center">
              <Title
                heading={4}
                className="break-all w-[10rem]"
                ellipsis={ellipsis}>
                {notice.Name}
              </Title>
            </div>
            <div>
              <Link
                className={cls('text-semi-color-text-2 text-base leading-5')}
                target={'_blank'}
                to={`/${scope}/billboard?id=${notice.Id}&title=${notice.Name}`}>
                <Space align="center" spacing={0}>
                  更多
                  <Rightarrow size={16} color="black" />
                </Space>
              </Link>
            </div>
          </div>
          <ul className={cls('mb-6')}>
            {renderNoticeItem(notice, Boolean(ifAdmin), scope)}
          </ul>
        </Panel>
      )}
    </>
  );
};

const OtherBoard: React.FC<{
  group: Group;
  ifAdmin: boolean;
  refetch: () => Promise<void>;
}> = ({ ifAdmin, refetch, group }) => {
  const biz = useBiz(s => s.biz);
  const scope = useScope();
  const { deletePostGroup } = useDeletePostGroup();
  const { deleteGroup } = useDeleteGroup();
  const { renderAddPostModal } = useInsertPostGroupModal();

  return (
    <>
      <Panel key={group.Id}>
        <div className="flex mr-auto mb-4 justify-between">
          <div className="font-medium text-xl flex items-center">
            <Title heading={4} className="max-w-[12rem]" ellipsis={ellipsis}>
              {group.Name}
            </Title>
            {ifAdmin && (
              <Popconfirm
                position="top"
                title="确认删除该看板吗？"
                onConfirm={async () => {
                  await deleteGroup(group.Id, biz);
                  await refetch();
                  Toast.success('删除成功');
                }}>
                <span className="ml-2 font-normal text-base leading-5 text-semi-color-text-1 hover:text-semi-color-primary cursor-pointer">
                  [删除]
                </span>
              </Popconfirm>
            )}
          </div>
          <div>
            <Link
              target={'_blank'}
              to={`/${scope}/billboard?id=${group.Id}&title=${group.Name}`}>
              <span
                className={cls(
                  'text-semi-color-text-2 font-normal text-base leading-5',
                )}>
                <Space align="center" spacing={0}>
                  更多
                  <Rightarrow size={16} color="black" />
                </Space>
              </span>
            </Link>
          </div>
        </div>
        <div className={'mb-6'}>
          {renderOtherItem(group, ifAdmin, scope, deletePostGroup, refetch)}
        </div>
        {ifAdmin && (
          <div>
            <Button
              className="bg-semi-color-fill-0 hover:text-semi-color-primary hover:bg-semi-color-primary-light-default w-full flex items-center justify-center text-semi-color-text-1"
              onClick={() => {
                renderAddPostModal(group.Id, refetch);
              }}>
              <Space align="center">
                <IconPlus className="mr-1" /> <div>添加帖子</div>
              </Space>
            </Button>
          </div>
        )}
      </Panel>
    </>
  );
};

export const Topic: React.FC = () => {
  const scope = useScope();
  const biz = useBiz(state => state.biz);
  const { ifAdmin } = useUserInfo();
  const { getPostsByGroup, posts } = useGetPostsByGroup();
  const { renderInsertGroupModal, triggerAddModal } = useInsertGroupModal();
  const { deleteGroup } = useDeleteGroup();

  const special = posts?.List.filter(v => v.GroupType === GroupType.Special);
  const history = useHistory();

  if (!special?.length && !ifAdmin) {
    return null;
  }

  return (
    <Panel>
      <div className="text-xl font-medium mb-6">专题推荐</div>
      <div className="flex flex-wrap">
        {special?.map(item => (
          <Button
            key={item.Id}
            onClick={() => {
              history.push(
                `/${scope}/billboard?id=${item.Id}&title=${item.Name}`,
              );
            }}
            type="tertiary"
            className={cls(style.special, 'mr-2 mb-3 rounded')}>
            <Space align="center">
              <IconHash
                size="small"
                className={cls(style.hash, 'text-inherit')}
              />
              <span className="truncate max-w-[10em]">{item.Name}</span>
              {ifAdmin && (
                <Popconfirm
                  position="top"
                  title={'删除该专题吗？'}
                  onConfirm={async () => {
                    await deleteGroup(item.Id, biz);
                    await getPostsByGroup();
                    Toast.success('删除成功');
                  }}>
                  <IconCrossStroked
                    onClick={e => e.stopPropagation()}
                    size="small"
                    className="text-semi-color-text-1 hover:text-semi-color-primary"
                  />
                </Popconfirm>
              )}
            </Space>
          </Button>
        ))}
      </div>
      {ifAdmin && (
        <div>
          <Button
            className="bg-semi-color-fill-0 w-full mt-3 hover:text-semi-color-primary hover:bg-semi-color-primary-light-default  flex items-center justify-center text-semi-color-text-1"
            onClick={() => {
              triggerAddModal(GroupType.Special, async () => {
                await getPostsByGroup();
              });
            }}>
            <Space align="center">
              <IconPlus className="mr-1" /> <div>添加专题</div>
            </Space>
          </Button>
          {renderInsertGroupModal()}
        </div>
      )}
    </Panel>
  );
};

export const NoticeCard: React.FC<{
  refetch: () => Promise<any>;
  data?: GetPostsByGroupResponseData;
  loading?: boolean;
}> = ({ refetch, loading, data }) => {
  const { ifAdmin } = useUserInfo();

  const notice = data?.List.find(v => v.Name === OFFICIAL);

  // 查看更多
  return (
    <Spin spinning={loading}>
      <div className={cls(style.board)}>
        {(notice?.PostList.length || ifAdmin) && (
          <NoticeBoard notice={notice} />
        )}

        {data?.List.filter(
          v => v.GroupType !== GroupType.Special && v.Name !== OFFICIAL,
        ).map(
          v =>
            (v.PostList.length || ifAdmin) && (
              <OtherBoard
                key={v.Id}
                group={v}
                ifAdmin={Boolean(ifAdmin)}
                refetch={refetch}
              />
            ),
        )}
      </div>
    </Spin>
  );
};

export const AddBoard: React.FC<{
  refetch: () => Promise<any>;
}> = ({ refetch }) => {
  const { renderInsertGroupModal: renderAddModal, triggerAddModal } =
    useInsertGroupModal();
  const { ifAdmin } = useUserInfo();

  if (!ifAdmin) {
    return null;
  }
  return (
    <div className="my-4">
      <Button
        style={{ height: 44 }}
        className="bg-semi-color-fill-0 w-full ring-0 py-3 hover:text-semi-color-primary hover:bg-semi-color-primary-light-default rounded flex items-center justify-center text-semi-color-text-1"
        onClick={() => {
          triggerAddModal(GroupType.Board, refetch);
        }}>
        <Space align="center">
          <IconPlus /> 新增看板
        </Space>
      </Button>
      {renderAddModal()}
    </div>
  );
};
